import merge from "@tinkoff/utils/object/mergeDeep";
import { match, P } from "ts-pattern";
import asArray from "../as-array";
import { getBucketSize, millisecondsToSeconds } from "../date";
import favoriteDataRequestToDropDownParams from "../transformers/favorite-data-request-to-drop-down-params";
import favoriteRequestTransformer from "../../lib/transformers/request-object-transformer";
import { FilterType } from "../types/filters";
import { findLabelForValue } from "./serialization";
/**
 * Return a FilterMap containing only the entries that have a value
 */
export function filterCollection(collection, predicate) {
    return Object.fromEntries(Object.entries(collection).filter(([, filter]) => predicate(filter)));
}
export function mapCollection(collection, fn) {
    return Object.fromEntries(Object.entries(collection).map(([id, filter]) => [id, fn(filter)]));
}
/**
 * Check whether a filter has a selected value - either a single value or an array with
 * at least one value.
 */
export function hasSelectedValue(filter) {
    const { selected } = filter;
    if (typeof selected === "undefined") {
        return false;
    }
    if (Array.isArray(selected)) {
        return selected.length > 0;
    }
    return selected.value !== "" && selected.value !== null;
}
/**
 * Check whether a filter has a valid set of options for the user to select.
 */
export function hasOptions(filter) {
    const { options, type } = filter;
    if (type === FilterType.DATE_RANGE) {
        //Date range filters do not need options to work properly.
        return true;
    }
    else if (options.length > 0) {
        return true;
    }
    else {
        return false;
    }
}
/**
 * Convert a filter value collection to a DataRequest object
 *
 * @param FilterCollection filters
 * @param boolean clampDateToBucket Whether to clamp relative dates to the previous bucket
 */
export function toDataRequest(filters, clampRelativeDates = false) {
    const dataRequest = {};
    const dataRequestFilters = {};
    const compareFilters = {};
    const filterValueArray = (filterValue) => {
        return asArray(filterValue)
            .map((v) => v.value)
            .filter(Boolean);
    };
    for (const id in filters) {
        const filter = filters[id];
        match(filter)
            .with({ type: FilterType.LIST, id: "stat", selected: P.optional(P.select()) }, (selected) => {
            dataRequest.aggregationType = asArray(selected)[0].value;
        })
            .with({ type: FilterType.LIST, selected: P.optional(P.select()) }, (selected) => {
            if (selected) {
                dataRequestFilters[id] = filterValueArray(selected);
            }
        })
            .with({ type: FilterType.TEXT, selected: P.optional(P.select()) }, (selected) => {
            if (selected) {
                dataRequestFilters[id] = filterValueArray(selected);
            }
        })
            .with({ type: FilterType.DATE_RANGE, selected: P.optional(P.select()) }, (selected) => {
            if (selected) {
                const value = selected.value;
                if ("secondsAgo" in value) {
                    // Convert relative dates into absolute dates
                    const { secondsAgo } = value;
                    let endDate = millisecondsToSeconds(Date.now());
                    let startDate = endDate - secondsAgo;
                    const bucketSize = getBucketSize(startDate, endDate);
                    if (clampRelativeDates) {
                        endDate = endDate - (endDate % bucketSize);
                        startDate = startDate - (startDate % bucketSize);
                    }
                    dataRequestFilters.dateRange = { startDate, endDate, secondsAgo };
                    if (value.compare) {
                        compareFilters.dateRange = {
                            startDate: dataRequestFilters.dateRange.startDate - secondsAgo,
                            endDate: dataRequestFilters.dateRange.endDate - secondsAgo,
                            secondsAgo,
                        };
                    }
                }
                else {
                    dataRequestFilters.dateRange = { startDate: value.startDate, endDate: value.endDate };
                    if (value.compareStartDate && value.compareEndDate) {
                        compareFilters.dateRange = {
                            startDate: value.compareStartDate,
                            endDate: value.compareEndDate,
                        };
                    }
                }
            }
        })
            .with({ type: FilterType.KEY_VALUE, selected: P.optional(P.select()) }, (selected) => {
            if (selected) {
                if (id === "customerData") {
                    dataRequestFilters.customerData = asArray(selected).map((option) => {
                        const { key, values } = option.value;
                        return {
                            name: key.value,
                            value: values.map((v) => v.value),
                        };
                    });
                }
                else {
                    dataRequestFilters[id] = asArray(selected).map((option) => {
                        const { key, values } = option.value;
                        return {
                            reference: key.value,
                            operator: values[0].value,
                        };
                    });
                }
            }
        })
            .with({ type: FilterType.THRESHOLD, selected: P.optional(P.select()) }, (selected) => {
            if (selected) {
                dataRequestFilters.metricBounds = [selected.value];
            }
        })
            .exhaustive();
    }
    if (Object.keys(dataRequestFilters).length) {
        dataRequest.filters = dataRequestFilters;
    }
    if (Object.keys(compareFilters).length) {
        dataRequest.compareFilters = compareFilters;
    }
    return dataRequest;
}
/**
 * Convert a DataRequest object to a FilterCollection
 */
export function fromDataRequest(config, dataRequest) {
    const { filters = {} } = dataRequest;
    // Correct filters where the ID in the data request does not match the ID in the config
    const correctFilterId = (id) => {
        if (id === "dateRange") {
            return "date";
        }
        return id;
    };
    const isValid = isValidFilter(config);
    // Build the selected options for each filter in the data request
    const selectedFilters = Object.fromEntries(Object.entries(filters)
        .filter(([id]) => isValid(correctFilterId(id)))
        .map(([id, filterValue]) => {
        const filterConfig = config[correctFilterId(id)];
        const selected = match([filterConfig, filterValue])
            .with([{ type: FilterType.DATE_RANGE }, P.any], ([config, value]) => {
            const date = value;
            return {
                label: findLabelForValue(config, date),
                value: date,
            };
        })
            .with([{ type: FilterType.KEY_VALUE }, P.array({ reference: P.union(P.string, P.number) })], ([config, values]) => {
            return values.map((value) => {
                const reference = String(value.reference);
                return {
                    label: "",
                    value: {
                        key: {
                            label: findLabelForValue(config, reference) || reference,
                            value: reference,
                        },
                        values: [
                            {
                                label: findLabelForValue(config, value.operator),
                                value: value.operator,
                            },
                        ],
                    },
                };
            });
        })
            .with([{ type: FilterType.KEY_VALUE }, P.array({ name: P.string })], ([config, values]) => {
            return values.map((cd) => {
                return {
                    label: "",
                    value: {
                        key: {
                            label: findLabelForValue(config, cd.name) || cd.name,
                            value: cd.name,
                        },
                        values: cd.value.map((value) => ({
                            label: findLabelForValue(config, String(value)),
                            value: String(value),
                        })),
                    },
                };
            });
        })
            .with([{ type: FilterType.THRESHOLD }, P.array({ metric: P.string })], ([config, values]) => {
            // MetricThresholdFilter also only supports a single value.
            const value = values[0];
            return {
                label: findLabelForValue(config, value.metric),
                value,
            };
        })
            .with([{ type: FilterType.LIST }, P.array(P.any)], ([config]) => {
            // Some legacy filter values have been stored as numbers. Ensure they are
            // converted to strings before processing them.
            const values = asArray(filterValue).map((v) => String(v));
            if (config.multiple) {
                return values.map((value) => ({
                    label: findLabelForValue(config, value),
                    value,
                }));
            }
            return {
                label: findLabelForValue(config, values[0]),
                value: values[0],
            };
        })
            .otherwise(() => {
            console.error("Invalid filter value", { id, config: filterConfig, value: filterValue });
            return {
                label: "",
                value: "",
            };
        });
        return [filterConfig.id, { selected }];
    }));
    return merge(config, selectedFilters);
}
/**
 * Convert a FilterCollection to legacy dropdown query params. The return value does not include the leading `?`.
 */
export function toDropdownQueryParams(filters) {
    const dataRequest = toDataRequest(filters);
    // Some of the legacy endpoints require the old dropdown parameters. We have to create a fake
    // data request, transform it into a favorites request, and then transform that into the legacy
    // dropdown query parameter string.
    const favoritesRequest = favoriteRequestTransformer(Object.assign(Object.assign({}, dataRequest), { chartId: "-", queryType: "timeseries", aggregationType: 50, metrics: ["-"] }));
    return favoriteDataRequestToDropDownParams(favoritesRequest);
}
export function toImproveDataQueryParams(filters) {
    const dataRequest = toDataRequest(filters);
    const params = new URLSearchParams();
    if (dataRequest.filters) {
        if (dataRequest.filters.browsers) {
            params.append("testprofile", dataRequest.filters.browsers[0]);
        }
        if (dataRequest.filters.pageLabels) {
            params.append("label", dataRequest.filters.pageLabels[0]);
        }
        if (dataRequest.filters.regions) {
            params.append("region", dataRequest.filters.regions[0]);
        }
        if (dataRequest.filters.siteIds) {
            params.append("site", dataRequest.filters.siteIds[0]);
        }
        if (dataRequest.filters.snapshotIds) {
            params.append("snapshot_id", dataRequest.filters.snapshotIds[0]);
        }
    }
    return params.toString();
}
/**
 * Generate a title for a FilterCollection based on its values. Optionally provide a "comparison"
 * FilterCollection.
 */
export function generateFilterSetTitle(filters, comparisonFilters = {}) {
    const getValues = (filter) => asArray(filter.selected).map((v) => JSON.stringify(v.value));
    const getLabels = (filter) => asArray(filter.selected).map((v) => getSelectedLabel(filter, v));
    const pluralFilterName = (filter) => {
        // Key-value filters use the variable name
        if (filter.type === FilterType.KEY_VALUE && filter.selected) {
            const selected = asArray(filter.selected)[0];
            return `${selected.value.key.label} Values`;
        }
        // Metric threshold filters use the metric name
        if (filter.type === FilterType.THRESHOLD && filter.selected) {
            return `${filter.selected.label} Values`;
        }
        // All other filters use the filter name
        return filter.namePlural || `${filter.name}s`;
    };
    const filterValuesAreIdentical = (id) => {
        if (!filters.hasOwnProperty(id) || !comparisonFilters.hasOwnProperty(id)) {
            return false;
        }
        const filterValues = getValues(filters[id]);
        const comparisonFilterValues = getValues(comparisonFilters[id]);
        if (filterValues.length !== comparisonFilterValues.length) {
            return false;
        }
        return filterValues.every((value) => comparisonFilterValues.includes(value));
    };
    const allFilterIds = new Set(Object.keys(filters).concat(Object.keys(comparisonFilters)));
    return Array.from(allFilterIds)
        .map((id) => {
        if (!filters.hasOwnProperty(id)) {
            // This filter is only present in the comparison filters
            return `All ${pluralFilterName(comparisonFilters[id])}`;
        }
        else if (!filterValuesAreIdentical(id)) {
            // This filter is unique across both filter sets
            return getLabels(filters[id]).join(", ");
        }
        // The filter value is the same in both filter sets
        return false;
    })
        .filter(Boolean)
        .join(", ");
}
export function getSelectedValue(filter, multiple) {
    var _a, _b;
    if (!filter || typeof filter.selected === "undefined") {
        return undefined;
    }
    const { selected } = filter;
    if (Array.isArray(selected)) {
        if (multiple) {
            return selected.filter((item) => looksLikeValidValue(item.value)).map((item) => item.value);
        }
        else {
            if (looksLikeValidValue((_a = selected[0]) === null || _a === void 0 ? void 0 : _a.value)) {
                return (_b = selected[0]) === null || _b === void 0 ? void 0 : _b.value;
            }
        }
    }
    const value = selected.value;
    return multiple ? [value] : value;
}
export function getSelectedLabel(filter, selected) {
    return (match(selected)
        // KeyValueValue
        .with({ value: { key: P.select("key"), values: P.select("values") } }, ({ key, values }) => {
        return `${getSelectedLabel(filter, key)}: ${getSelectedLabel(filter, values[0])}`;
    })
        // MetricBoundsValue
        .with({ value: { metric: P.string, lowerBound: P.optional(P.number), upperBound: P.optional(P.number) } }, (selected) => {
        const metricOption = flattenOptionGroup(filter.options).find((option) => option.value === selected.value.metric);
        const suffix = metricOption ? metricOption.units : "";
        const lowerBound = typeof selected.value.lowerBound === "undefined"
            ? "Any"
            : `${selected.value.lowerBound}${suffix}`;
        const upperBound = typeof selected.value.upperBound === "undefined"
            ? "Any"
            : `${selected.value.upperBound}${suffix}`;
        return `${selected.label}: ${lowerBound} - ${upperBound}`;
    })
        .with(P.not(undefined), (selected) => {
        // Any other values with a label
        if (looksLikeValidValue(selected.label) && selected.label) {
            return selected.label;
        }
        // Values without a label
        if (looksLikeValidValue(selected.value) && selected.value) {
            return selected.value;
        }
        // Unexpected value(?)
        return "";
    })
        .otherwise(() => {
        // Fallback for malformed / unexpected values
        return "";
    }));
}
export function scopeFilter(config, selectedFilters, filter) {
    return match(filter)
        .with(
    // Filters with option groups
    { scopedBy: P.string, options: P.array({ options: P.array({ value: P.any }) }) }, () => {
        throw new Error("NotImplementedError: Option group filters cannot be scoped.");
    })
        .with(
    // Filters with flat options
    { scopedBy: P.string, options: P.array({ value: P.any }) }, (filter) => {
        const scope = selectedFilters[filter.scopedBy];
        if (!scope || !scope.selected) {
            return filter;
        }
        // We know the selected scope value will be string options, so we can explciitly
        // type it as such.
        const selectedScopes = asArray(scope.selected);
        const allOptions = config[filter.id].options;
        const scopedOptions = allOptions.filter((option) => {
            if (option.inScopes) {
                return selectedScopes.some((scope) => option.inScopes.includes(scope.value));
            }
            return true;
        });
        const validSelectedOptions = asArray(filter.selected).filter((option) => scopedOptions.some((scopedOption) => scopedOption.value === option.value));
        const newSelectedOptions = validSelectedOptions.length ? validSelectedOptions : scopedOptions;
        // If the scoped options are empty for some reason, we fall back to showing all options
        const newOptions = scopedOptions.length ? scopedOptions : filter.options;
        // If the selected option is not scoped, we fall back to the first scoped option
        const newSelected = Array.isArray(filter.selected) ? newSelectedOptions : newSelectedOptions[0];
        return Object.assign(Object.assign({}, filter), { options: newOptions, selected: newSelected });
    })
        .otherwise((filter) => {
        return filter;
    });
}
/**
 * Determine whether a filter ID is valid for the given filter collection.
 */
export function isValidFilter(config) {
    return function (id) {
        return config.hasOwnProperty(id);
    };
}
/**
 * Determine whether an option is a FilterOptionGroup
 */
export function isOptionGroup(option) {
    return "title" in option;
}
/**
 * Determine whether an array of options is an array of FilterOptionGroup.
 * Required due to TS issue with .every()
 * @see https://github.com/microsoft/TypeScript/issues/44373
 */
export function isOptionGroupArray(options) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return options.every(isOptionGroup);
}
export function flattenOptionGroup(groups) {
    return groups.flatMap((group) => group.options);
}
function looksLikeValidValue(value) {
    return typeof value === "string" || typeof value === "number";
}
